import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { GrossAdjustment } from 'models/TransactionEstimates'

/**
 * A GrossAdjustment is an object that contains all the fee data that we track for DCF
 * This includes "applied" values => numbers that were used to calculate the fees
 * As well as "raw" values => individually estimated amounts for the various fees
 * Finally, "raw_initial_gross_amount" is used to track the "intended donation amount"
 * OR the amount before DCF was added on top
 * For a transaction where donor did NOT cover fees, all GrossAdjustment values should be null
 */
const initialState: GrossAdjustment = {
  applied_application_fee_percent: null,
  applied_processor_fee_percent: null,
  applied_raw_processor_fee_flat: null,
  applied_flex_rate_percent: null,
  raw_application_fee_amount: null,
  raw_processor_fee_amount: null,
  raw_flex_rate_amount: null,
  raw_initial_gross_amount: null,
}

export const grossAdjustmentSlice = createSlice({
  name: 'grossAdjustment',
  initialState,
  reducers: {
    setAppliedApplicationFeePercent: (state, action: PayloadAction<number | null>) => {
      state.applied_application_fee_percent = action.payload
    },
    setAppliedProcessorFeePercent: (state, action: PayloadAction<number | null>) => {
      state.applied_processor_fee_percent = action.payload
    },
    setAppliedRawProcessorFeeFlat: (state, action: PayloadAction<number | null>) => {
      state.applied_raw_processor_fee_flat = action.payload
    },
    setAppliedFlexRatePercent: (state, action: PayloadAction<number | null>) => {
      state.applied_flex_rate_percent = action.payload
    },
    setRawApplicationFeeAmount: (state, action: PayloadAction<number | null>) => {
      state.raw_application_fee_amount = action.payload
    },
    setRawProcesserFeeAmount: (state, action: PayloadAction<number | null>) => {
      state.raw_processor_fee_amount = action.payload
    },
    setRawFlexRateAmount: (state, action: PayloadAction<number | null>) => {
      state.raw_flex_rate_amount = action.payload
    },
    setRawInitialGrossAmount: (state, action: PayloadAction<number | null>) => {
      state.raw_application_fee_amount = action.payload
    },
    // Reducer to update the gross adjustment all at once
    updateGrossAdjustment: (state, action: PayloadAction<Partial<GrossAdjustment>>) => {
      return { ...state, ...action.payload }
    },
    // Reducer to clear the gross adjustment all at once
    clearGrossAdjustment: () => {
      return initialState
    },
  },
})

export const {
  setAppliedApplicationFeePercent,
  setAppliedProcessorFeePercent,
  setAppliedRawProcessorFeeFlat,
  setAppliedFlexRatePercent,
  setRawApplicationFeeAmount,
  setRawProcesserFeeAmount,
  setRawFlexRateAmount,
  setRawInitialGrossAmount,
  updateGrossAdjustment,
  clearGrossAdjustment,
} = grossAdjustmentSlice.actions

export default grossAdjustmentSlice.reducer
